import React, { useState } from 'react';
import './ReplaysList.css';

const ReplaysList = ({ replays, onReplaySelect, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('replay_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const REPLAYS_PER_PAGE = 50;

  const filteredReplays = replays
    .filter(replay => 
      replay.map_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      replay.winning_player.toLowerCase().includes(searchTerm.toLowerCase()) ||
      replay.format.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'replay_date') {
        comparison = new Date(a.replay_date) - new Date(b.replay_date);
      } else if (sortBy === 'map_name') {
        comparison = a.map_name.localeCompare(b.map_name);
      } else if (sortBy === 'game_length') {
        comparison = a.game_length - b.game_length;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredReplays.length / REPLAYS_PER_PAGE);
  const startIndex = (currentPage - 1) * REPLAYS_PER_PAGE;
  const endIndex = startIndex + REPLAYS_PER_PAGE;
  const paginatedReplays = filteredReplays.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="replays-list">
      <div className="controls">
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search replays..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={onRefresh} className="refresh-btn">
            Refresh
          </button>
        </div>
        
        <div className="sort-controls">
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="replay_date">Date</option>
            <option value="map_name">Map</option>
            <option value="game_length">Duration</option>
          </select>
          <button 
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <div className="replays-count">
        Showing {paginatedReplays.length} of {filteredReplays.length} replays (Page {currentPage} of {totalPages})
      </div>

      <div className="replays-grid">
        {paginatedReplays.map(replay => (
          <div 
            key={replay.replay_id} 
            className="replay-card"
            onClick={() => onReplaySelect(replay)}
          >
            <div className="replay-header">
              <h3>{replay.map_name}</h3>
              <span className="replay-id">#{replay.replay_id}</span>
            </div>
            
            <div className="replay-info">
              <div className="info-row">
                <span className="label">Date:</span>
                <span>{formatDate(replay.replay_date)}</span>
              </div>
              <div className="info-row">
                <span className="label">Duration:</span>
                <span>{formatDuration(replay.game_length)}</span>
              </div>
              <div className="info-row">
                <span className="label">Format:</span>
                <span>{replay.format}</span>
              </div>
              <div className="info-row">
                <span className="label">Winner:</span>
                <span className="winner">{replay.winning_player}</span>
              </div>
              <div className="info-row">
                <span className="label">Type:</span>
                <span>{replay.game_type}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ← Previous
          </button>
          
          <div className="pagination-info">
            <span>Page {currentPage} of {totalPages}</span>
          </div>
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default ReplaysList;

